import { IconChevronRightSmall } from "@/assets/icons/geist/IconChevronRightSmall";
import { useEffect, type FC, type ReactNode } from "react";
import { Link, useMatches, useNavigate, type UIMatch } from "react-router-dom";
import type { BreadcrumbItem } from "@/contexts/SettingsPageContext";

type BreadcrumbsProps = {
    label?: string | ReactNode;
    customBreadcrumbs?: BreadcrumbItem[] | null;
};

const Breadcrumbs: FC<BreadcrumbsProps> = ({ label, customBreadcrumbs }) => {
    const matches = useMatches() as UIMatch<unknown, { label: string; pathname: string; description?: string }>[];
    const crumbs = matches.filter((match) => Boolean(match.handle));
    const parents = crumbs.slice(0, -1);
    const current = crumbs[crumbs.length - 1];
    const navigate = useNavigate();
    const pathOfParentPage = parents.length > 0 && parents[parents.length - 1].pathname;
    // capture escape key navigate to the previous part of the breadcrumb
    useEffect(() => {
        if (!pathOfParentPage) {
            return;
        }
        const handleKeyDown = (e: KeyboardEvent) => {
            // We only want to handle the event if no other element is focused
            if (document.activeElement?.nodeName !== "BODY") {
                return;
            }

            if (e.key === "Escape") {
                e.stopImmediatePropagation();
                e.stopPropagation();
                e.preventDefault();
                navigate(pathOfParentPage);
            }
        };
        window.addEventListener("keydown", handleKeyDown, { capture: true });
        return () => {
            window.removeEventListener("keydown", handleKeyDown, { capture: true });
        };
    }, [navigate, pathOfParentPage, current]);

    // Build the final breadcrumb items
    let finalBreadcrumbs: { label: ReactNode; to?: string; description?: string }[] = [];

    if (customBreadcrumbs && customBreadcrumbs.length > 0) {
        // Use parent breadcrumbs from routes + custom breadcrumbs
        finalBreadcrumbs = [...parents.map((p) => ({ label: p.handle?.label, to: p.pathname })), ...customBreadcrumbs];
    } else {
        // Use auto-generated breadcrumbs from routes
        finalBreadcrumbs = [
            ...parents.map((p) => ({ label: p.handle?.label, to: p.pathname })),
            { label: current?.handle?.label ?? label },
        ];
    }

    return (
        <ol className="flex min-w-0 flex-row items-center gap-0.5 text-lg font-bold">
            {finalBreadcrumbs.map((crumb, index) => {
                const isLast = index === finalBreadcrumbs.length - 1;

                if (isLast || !crumb.to) {
                    // Last item or item without link - just display as text
                    return (
                        <li
                            key={index}
                            className="flex min-w-0 items-center gap-2 text-2xl font-semibold tracking-[-0.2px] text-content-primary"
                        >
                            <span className="truncate">{crumb.label}</span>
                            {crumb.description && (
                                <span className="text-xl font-medium text-content-muted">{crumb.description}</span>
                            )}
                        </li>
                    );
                }

                // Item with link
                return (
                    <li
                        key={index}
                        className="flex flex-row items-center gap-[2px] text-2xl font-normal tracking-[-0.2px] text-content-primary"
                    >
                        <Link to={crumb.to}>{crumb.label}</Link>
                        <IconChevronRightSmall size="lg" className="text-content-muted" />
                    </li>
                );
            })}
        </ol>
    );
};

export default Breadcrumbs;
